import React, { memo, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import Logout from '@mui/icons-material/Logout';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import { useTheme as useThemeMode } from '../../contexts/ThemeContext';

/** "andrew.p@shipium.com" -> "AP" */
export const userInitials = (username: string): string =>
  username
    .split('@')[0]
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

const UserAvatar = memo(function UserAvatar({ username }: { username: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: 16,
        bgcolor: 'brand.container',
        color: 'brand.onContainer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      {userInitials(username)}
      <Box
        component="span"
        sx={{
          position: 'absolute',
          right: -1,
          bottom: -1,
          width: 10,
          height: 10,
          borderRadius: 5,
          bgcolor: 'brand.statusDot',
          border: 2,
          borderColor: 'background.paper',
        }}
      />
    </Box>
  );
});

interface AccountMenuProps {
  username: string;
  onLogout: () => void;
}

const AccountMenu: React.FC<AccountMenuProps> = ({ username, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { themeMode, toggleTheme } = useThemeMode();
  const open = Boolean(anchorEl);
  const close = useCallback(() => setAnchorEl(null), []);
  const ExpandIcon = open ? ExpandLess : ExpandMore;

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${username}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          py: 0.5,
          pl: 0.5,
          pr: 1.75,
          borderRadius: 6,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          textAlign: 'left',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <UserAvatar username={username} />
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
          <Typography component="span" noWrap sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.2, color: 'text.primary' }}>
            {username}
          </Typography>
          <Typography component="span" sx={{ fontSize: 11, lineHeight: 1.2, color: 'success.main' }}>
            Authenticated
          </Typography>
        </Box>
        <ExpandIcon sx={{ fontSize: 20, color: 'text.secondary', ml: 0.25 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, minWidth: 260, borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{username}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: 'success.main' }}>
            <VerifiedUser sx={{ fontSize: 16 }} />
            Authenticated
          </Box>
        </Box>
        <Divider sx={{ mb: 0.75 }} />
        <MenuItem onClick={toggleTheme} sx={{ height: 44 }}>
          <ListItemIcon>{themeMode === 'dark' ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}</ListItemIcon>
          <ListItemText>{themeMode === 'dark' ? 'Light mode' : 'Dark mode'}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            onLogout();
          }}
          sx={{ height: 44 }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(AccountMenu);
